import { mergeLeft } from 'ramda'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { AnnotationClasses } from '../../types'
import { AnnotationType } from '../../types/annotation'
import FiltersModal from '../FiltersModal/FiltersModal'
import { ObservationsParams } from '../Observations/types'
import { USER_LEVEL } from '../Settings/definitions'
import { SettingsContext } from '../Settings/SettingsContext'
import AnnotationClassFilter from './AnnotationClassFilter/AnnotationClassFilter'
import AnnotationStatusFilter from './AnnotationStatusFilter'
import AvailableForTestingFilter from './AvailableForTestingFilter/AvailableForTestingFilter'
import { getAvailableForTestingParam } from './AvailableForTestingFilter/utils'
import DatasetFilter from './DatasetFilter/DatasetFilter'
import ImageNameFilter from './ImageNameFilter'

type Filters = ObservationsParams

type ObservationsBrowserFiltersProps = {
  productId: number
  trainingId?: number
  initialValues: Partial<Filters>
  onSave: (filters: Partial<Filters>) => void
  onRemove: () => void
  useAvailabilityFilter?: boolean
  annotationType: AnnotationType
  disabled?: boolean
}

export const ObservationsBrowserFilters = ({
  productId,
  trainingId,
  initialValues,
  onSave,
  onRemove,
  useAvailabilityFilter,
  annotationType,
  disabled = false,
}: ObservationsBrowserFiltersProps) => {
  const { t } = useTranslation()
  const { settings } = useContext(SettingsContext)
  const isExpertUser = settings.preference__user_level === USER_LEVEL.expert

  const labels = {
    annotationClasses: t('Annotation status'),
    productClasses: t('Class'),
    availableForTesting: t('Testing availability'),
    filename: t('Image Name'),
    dataset: t('Dataset'),
    datasetExclude: t('Exclude mode'),
  }

  return (
    <FiltersModal<Filters>
      initialValues={initialValues}
      labels={labels}
      onSave={onSave}
      onRemove={onRemove}
      removeDisabled
      disabled={disabled}
    >
      {({ filters, setFilters }) => (
        <>
          <ImageNameFilter
            onChange={(v) => {
              setFilters(
                mergeLeft({
                  filename: v,
                })
              )
            }}
            initialValue={filters?.filename}
          />
          {isExpertUser && (
            <DatasetFilter
              productId={productId}
              onChange={(vs) =>
                setFilters(
                  mergeLeft({
                    dataset: vs.map((v) => v.value),
                  })
                )
              }
              onExcludeChange={(exclude) =>
                setFilters(
                  mergeLeft({
                    datasetExclude: exclude,
                  })
                )
              }
              initialValue={filters?.dataset}
              initialExclude={filters?.datasetExclude}
            />
          )}
          <AnnotationStatusFilter
            onChange={(v) => {
              setFilters(
                mergeLeft({
                  annotationClasses: v?.value as AnnotationClasses | undefined,
                })
              )
              setFilters(
                mergeLeft({
                  productClasses: undefined,
                })
              )
            }}
            initialValue={
              filters?.productClasses ? 'DEFINED' : filters.annotationClasses
            }
            annotationType={annotationType}
          />
          {(filters.annotationClasses === 'DEFINED' ||
            filters?.productClasses) && (
            <AnnotationClassFilter
              productId={productId}
              onChange={(vs) =>
                setFilters(
                  mergeLeft({
                    productClasses: vs.map((v) => v.value),
                  })
                )
              }
              initialValue={filters?.productClasses}
            />
          )}
          {useAvailabilityFilter && (
            <AvailableForTestingFilter
              onChange={(v) =>
                setFilters(
                  mergeLeft(getAvailableForTestingParam(trainingId, v?.value))
                )
              }
              defaultValue={
                filters.availableForTesting ? 'available' : undefined
              }
            />
          )}
        </>
      )}
    </FiltersModal>
  )
}
